/**
 * @graphql cc
 */
import type { FC } from 'react';
import React, { useContext, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import type { SelectProps } from '@atlaskit/select';
import Select from '@atlaskit/select';

import { FiltersContext } from './FiltersContext.tsx';
import { LabelsQuery } from './LabelsQuery.graphql.ts';
import type {
	SpaceDirectoryLabelsQuery as LabelsQueryType,
	SpaceDirectoryLabelsQueryVariables as LabelsQueryVariables,
	SpaceDirectoryLabelsQuery_teamLabels_nodes,
} from './__types__/LabelsQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const LABELS_FILTER_DESCRIPTION_ID = 'labels-filter-description';

type Option = {
	label: string;
	value?: string;
};

const i18n = defineMessages({
	allCategories: {
		id: 'space-directory.filters.labels.all-categories',
		defaultMessage: 'All categories',
		description: 'Name of a filter that will show all spaces regardless of their category',
	},
	categoryFilterLabel: {
		id: 'space-directory.filters.labels.aria-labelledby.text',
		defaultMessage: 'Filter by category',
		description: 'Label of the Space directory category filter combobox',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ScreenReaderOnlyText = styled.span({
	border: 0,
	clip: 'rect(1px, 1px, 1px, 1px)',
	'clip-path': 'inset(50%)',
	height: '1px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	margin: '-1px',
	overflow: 'hidden',
	padding: '0',
	position: 'absolute',
	width: '1px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	'word-wrap': 'normal !important',
});

const selectStyles = {
	container: (base) => ({
		...base,
		width: '142px',
		minHeight: '32px',
		marginLeft: '9px',
	}),
	control: (base) => ({
		...base,
		minHeight: '32px',
		height: '32px',
		flexWrap: 'nowrap',
	}),
};

export const LabelsFilter: FC = () => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { data, loading, fetchMore } = useQueryInterop<LabelsQueryType, LabelsQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-relay-graphql-operations -- Preloaded GraphQL Operation
		LabelsQuery,
		{
			variables: { start: 0 },
			notifyOnNetworkStatusChange: true,
		},
	);

	const {
		filters: { label },
		updateLabel,
	} = useContext(FiltersContext);

	const labelOptions = [
		{ label: intl.formatMessage(i18n.allCategories), value: '' },
		...((data?.teamLabels?.nodes as SpaceDirectoryLabelsQuery_teamLabels_nodes[]) || []).map(
			(labelItem) => ({
				label: labelItem?.name || '',
				value: labelItem?.name || undefined,
			}),
		),
	];

	const selectedOption = {
		label: label || intl.formatMessage(i18n.allCategories),
		value: label,
	};

	const onLabelsMenuScrollToBottom = () => {
		const hasNextPage = data?.teamLabels?.pageInfo?.hasNextPage || false;

		if (!loading && hasNextPage) {
			void fetchMore({
				variables: {
					start: data?.teamLabels?.nodes?.length || 0,
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					const newLabels = fetchMoreResult?.teamLabels?.nodes || [];
					const newPageInfo = fetchMoreResult?.teamLabels?.pageInfo || prev.teamLabels!.pageInfo;
					const oldLabels = prev?.teamLabels?.nodes || [];

					return {
						...prev,
						teamLabels: {
							nodes: [...oldLabels, ...newLabels],
							pageInfo: newPageInfo,
							__typename: 'PaginatedLabelList',
						},
					};
				},
			});
		}
	};

	const onLabelsChange: SelectProps<Option>['onChange'] = (option) => {
		updateLabel((option as Option).value);
	};

	return (
		<>
			<ScreenReaderOnlyText id={LABELS_FILTER_DESCRIPTION_ID} aria-hidden="true">
				{intl.formatMessage(i18n.categoryFilterLabel)}
			</ScreenReaderOnlyText>
			<Select
				styles={selectStyles}
				isLoading={loading && isOpen}
				options={labelOptions}
				onChange={onLabelsChange}
				value={selectedOption}
				onMenuScrollToBottom={onLabelsMenuScrollToBottom}
				onMenuOpen={() => setIsOpen(true)}
				onMenuClose={() => setIsOpen(false)}
				testId="space-labels"
				inputId="space-labels"
				classNamePrefix="react-select"
				labelId={LABELS_FILTER_DESCRIPTION_ID}
				menuPortalTarget={document.body}
			/>
		</>
	);
};
